import { useSearchDateShortcut } from '@/hooks/useSearch'
import type { CrudSearchItem, CrudTableColumn } from '@/types/crud-config'
import type { DepositCustomerRecord } from './deposit-customer.service'

const controlClass = 'deposit-customer-page__search-control'
const paymentChannelOptions = [
  { value: 'wallet', label: '钱包' },
  { value: 'mock', label: '模拟支付' },
  { value: 'alipay_qr', label: '支付宝扫码支付' },
  { value: 'alipay_bar', label: '支付宝条码支付' },
  { value: 'alipay_app', label: '支付宝 App 支付' },
  { value: 'alipay_wap', label: '支付宝 Wap 网站支付' },
  { value: 'alipay_pc', label: '支付宝 PC 网站支付' },
  { value: 'wx_bar', label: '微信条码支付' },
  { value: 'wx_native', label: '微信扫码支付' },
  { value: 'wx_app', label: '微信 App 支付' },
  { value: 'wx_lite', label: '微信小程序支付' },
  { value: 'wx_pub', label: '微信公众号支付' },
  { value: 'wx_wap', label: '微信 Wap 网站支付' }
]

export const depositCustomerSearchItems: CrudSearchItem[] = [
  {
    label: '客户名称/昵称',
    prop: 'customerKeyword',
    type: 'input',
    controlClass,
    clearable: true,
    placeholder: '请输入客户名称或昵称'
  },
  {
    label: '客户unionId',
    prop: 'unionid',
    type: 'input',
    controlClass,
    clearable: true,
    placeholder: '请输入客户unionId'
  },
  {
    label: '数据来源',
    prop: 'businessSourceName',
    type: 'input',
    controlClass,
    clearable: true,
    placeholder: '请输入数据来源'
  },
  {
    label: '所属组',
    prop: 'deptName',
    type: 'input',
    controlClass,
    clearable: true,
    placeholder: '请输入所属组'
  },
  {
    label: '所属员工',
    prop: 'empName',
    type: 'input',
    controlClass,
    clearable: true,
    placeholder: '请输入所属员工'
  },
  {
    label: '商品名',
    prop: 'productName',
    type: 'input',
    controlClass,
    clearable: true,
    placeholder: '请输入商品名'
  },
  {
    label: '支付渠道',
    prop: 'paymentChannel',
    type: 'select',
    controlClass,
    clearable: true,
    placeholder: '请选择支付渠道',
    options: paymentChannelOptions
  },
  {
    label: '是否退款',
    prop: 'refunded',
    type: 'select',
    controlClass,
    clearable: true,
    placeholder: '请选择是否退款',
    options: [
      { label: '未退款', value: 0 },
      { label: '已退款', value: 1 }
    ]
  },
  {
    label: '是否已交尾款',
    prop: 'balanceStatus',
    type: 'select',
    controlClass,
    clearable: true,
    placeholder: '请选择尾款状态',
    options: [
      { label: '未交尾款', value: 0 },
      { label: '已交尾款', value: 1 }
    ]
  },
  {
    label: '下单时间',
    prop: 'orderTime',
    type: 'dateTimeRange',
    controlClass: 'deposit-customer-page__date-control',
    columnSpan: 2,
    clearable: true,
    valueFormat: 'YYYY-MM-DD HH:mm:ss',
    shortcuts: useSearchDateShortcut(),
    startPlaceholder: '开始时间',
    endPlaceholder: '结束时间'
  }
]

const showText = (value: unknown) =>
  value === null || value === undefined || value === '' ? '-' : String(value)

const showMoney = (value: number | null) =>
  value === null ? '-' : `¥${value.toFixed(2)}`

const showStatus = (
  value: number | null,
  negativeText: string,
  positiveText: string
) => (value === 0 ? negativeText : value === 1 ? positiveText : '-')

const paymentChannelNameByValue = new Map(
  paymentChannelOptions.map((item) => [item.value, item.label])
)

const showPaymentChannel = (value: string) =>
  paymentChannelNameByValue.get(value) ?? showText(value)

export const depositCustomerTableColumns: CrudTableColumn<DepositCustomerRecord>[] =
  [
    {
      prop: 'customerName',
      label: '客户名称/昵称',
      slot: 'customer',
      minWidth: 180,
      align: 'left',
      showOverflowTooltip: true
    },
    {
      prop: 'unionid',
      label: '客户unionId',
      minWidth: 180,
      showOverflowTooltip: true,
      formatter: (row) => showText(row.unionid)
    },
    {
      prop: 'businessSourceName',
      label: '数据来源',
      minWidth: 120,
      showOverflowTooltip: true,
      formatter: (row) => showText(row.businessSourceName)
    },
    {
      prop: 'productName',
      label: '商品名',
      minWidth: 180,
      showOverflowTooltip: true,
      formatter: (row) => showText(row.productName)
    },
    {
      prop: 'depositAmount',
      label: '定金金额',
      minWidth: 110,
      formatter: (row) => showMoney(row.depositAmount)
    },
    {
      prop: 'balanceStatus',
      label: '是否已交尾款',
      minWidth: 120,
      formatter: (row) => showStatus(row.balanceStatus, '未交尾款', '已交尾款')
    },
    {
      prop: 'balanceAmount',
      label: '尾款金额',
      minWidth: 110,
      formatter: (row) => showMoney(row.balanceAmount)
    },
    {
      prop: 'deptName',
      label: '所属组',
      minWidth: 120,
      showOverflowTooltip: true,
      formatter: (row) => showText(row.deptName)
    },
    {
      prop: 'empName',
      label: '所属员工',
      minWidth: 120,
      showOverflowTooltip: true,
      formatter: (row) => showText(row.empName)
    },
    {
      prop: 'orderTime',
      label: '下单时间',
      minWidth: 165,
      formatter: (row) => showText(row.orderTime)
    },
    {
      prop: 'depositTime',
      label: '定金支付时间',
      minWidth: 165,
      formatter: (row) => showText(row.depositTime)
    },
    {
      prop: 'paymentStatusName',
      label: '支付状态',
      minWidth: 110,
      formatter: (row) => showText(row.paymentStatusName)
    },
    {
      prop: 'paymentChannel',
      label: '支付渠道',
      minWidth: 120,
      formatter: (row) => showPaymentChannel(row.paymentChannel)
    },
    {
      prop: 'refunded',
      label: '是否退款',
      minWidth: 100,
      formatter: (row) => showStatus(row.refunded, '未退款', '已退款')
    },
    {
      prop: 'refundAmount',
      label: '退款金额',
      minWidth: 110,
      formatter: (row) => showMoney(row.refundAmount)
    }
  ]
